import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import GallerySection from "../components/GallerySection";

export default function About() {
  return (
    <>
      <SEOHead
        title="About - Prajwal Halwai"
        description="Senior software engineering consultant offering direct collaboration, technical architecture, and product mindset based in Odense, Denmark."
        image="/_assets/profile.webp"
      />

      {/* Hero Section */}
      <section className="py-20 md:py-24 border-b border-[#181B24] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
                About Prajwal Halwai
              </span>
              <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight">
                Software engineer & product partner
              </h1>
              <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed">
                I help founders and established businesses move from initial concept to reliable, scalable digital products - with full architectural accountability and direct senior collaboration.
              </p>
              
              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] transition-all shadow-lg shadow-[#CC3737]/20"
                >
                  <span>Discuss your idea</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
                <Link
                  to="/work"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-medium text-white bg-[#141720] hover:bg-[#1E232F] border border-[#272E3D] transition-all"
                >
                  <span>View my work</span>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 flex justify-center">
              <div className="relative w-full max-w-sm rounded-2xl overflow-hidden bg-[#13161F] border border-[#282F3E] shadow-2xl">
                <img
                  src="/_assets/profile.webp"
                  alt="Prajwal Halwai - Software Engineer & Product Partner"
                  className="w-full h-auto aspect-[4/5] object-cover object-top"
                  loading="eager"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Professional Story Section */}
      <section className="py-20 border-b border-[#181B24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
                The story behind
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Engineering depth meets an owner's commercial perspective.
              </h2>
              
              <div className="space-y-4 text-sm sm:text-base text-[#94A3B8] leading-relaxed">
                <p>
                  I’ve spent years building software at small to large companies. I witnessed first-hand why so many software projects derail: either they get trapped in endless corporate meetings, or developers build solutions without understanding the actual business mechanics.
                </p>
                <p>
                  In addition to client work, I help run an e-commerce business, from strategy to daily execution. That operational reality shapes how I engineer software: I build products that are resilient, fast, and oriented around tangible business objectives.
                </p>
                <p>
                  When you collaborate with me, you speak directly with the engineer making the technical decisions. No middlemen, no account executives, and no junior handoffs.
                </p>
              </div>

              {/* Verified details presented cleanly without box containers */}
              <div className="pt-6 border-t border-[#1F2534] grid grid-cols-2 gap-4 text-xs">
                <div>
                  <span className="text-[#64748B] block mb-1">Company Registration</span>
                  <span className="text-white font-medium">CVR: 46152085</span>
                </div>
                <div>
                  <span className="text-[#64748B] block mb-1">Location</span>
                  <span className="text-white font-medium">Odense C, Denmark</span>
                </div>
                <div>
                  <span className="text-[#64748B] block mb-1">Phone (Clickable)</span>
                  <a href="tel:+4550382138" className="text-[#CC3737] hover:underline whitespace-nowrap">
                    +45 50 38 21 38
                  </a>
                </div>
                <div>
                  <span className="text-[#64748B] block mb-1">Direct Email</span>
                  <a href="mailto:prajwal@prajwalhalwai.com" className="text-white hover:underline whitespace-nowrap">
                    prajwal@prajwalhalwai.com
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-6">
              <div className="rounded-2xl overflow-hidden bg-[#141722] border border-[#232A39] shadow-xl">
                <img
                  src="/_assets/working-1.webp"
                  alt="Prajwal Halwai at workstation"
                  className="w-full h-auto aspect-[16/10] object-cover"
                />
              </div>

              {/* Core Advantages - presented openly without cards/box wrappers */}
              <div className="space-y-4 pt-2">
                {[
                  {
                    title: "Direct senior engineering",
                    desc: "You have direct access to senior technical experience on every video call, PR review, and architecture decision."
                  },
                  {
                    title: "Pragmatic architecture",
                    desc: "No unnecessary microservices or over-engineering. We build solid foundations that can be maintained and scaled with ease."
                  },
                  {
                    title: "Fast iterations",
                    desc: "Weekly sprints with live staging links so you can click through real software and provide feedback in real-time."
                  }
                ].map((item, idx) => (
                  <div key={idx} className="space-y-1">
                    <h3 className="text-sm font-bold text-white">{item.title}</h3>
                    <p className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Process from about page */}
      <section className="py-20 bg-[#0C0E14] border-b border-[#181B24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              How I deliver
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">
              Software development lifecycle
            </h2>
            <p className="text-sm text-[#94A3B8] mt-2">
              A disciplined, transparent progression that removes ambiguity at every stage.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Define", desc: "Understanding the problem, user journeys, and key business metrics before opening the editor." },
              { title: "Design", desc: "Technical schema design, API structure, and wireframes to validate layout and logic early." },
              { title: "Refine", desc: "Iterating on feedback, edge cases, security considerations, and performance bottlenecks." },
              { title: "Build", desc: "Writing clean, documented, tested code with continuous integration and seamless deployment." }
            ].map((step, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-[#11141D] border border-[#1F2534]"
              >
                <span className="text-xs font-mono text-[#CC3737]">Stage 0{idx + 1}</span>
                <h3 className="text-lg font-bold text-white mt-2 mb-2">{step.title}</h3>
                <p className="text-xs sm:text-sm text-[#94A3B8] leading-relaxed">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Connected About & Studio Gallery */}
      <GallerySection
        gallerySlug="about-gallery"
        title="Studio & Working Environment"
        subtitle="Behind the scenes in Odense, Denmark - tools, setup, and software development."
      />

      {/* Call to action */}
      <section className="py-20 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Ready to start a project?
          </h2>
          <p className="text-sm sm:text-base text-[#94A3B8]">
            I am available for hire or freelance worldwide. Let's schedule a conversation.
          </p>
          <div className="pt-2">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] transition-all shadow-lg shadow-[#CC3737]/20"
            >
              <span>Get in touch</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
